import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CalendarClock, Clock, Send, ChevronLeft, ChevronRight, Users, Ticket, Layers } from 'lucide-react';
import emailCampaignsApi from 'api/requests/emailCampaigns';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import EmailPreviewFrame from './EmailPreviewFrame';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const AUDIENCE_ICONS = {
    customers: Users,
    event_participants: Ticket,
    event_combination: Layers,
};

const STATUS_LABELS = {
    scheduled: 'Agendada',
    queued: 'Na fila',
    resolving: 'Calculando público',
    sending: 'Enviando',
    sent: 'Enviada',
    failed: 'Falhou',
    canceled: 'Cancelada',
};

const RECIPIENT_STATUS_LABELS = {
    pending: 'Pendente',
    sent: 'Enviado',
    failed: 'Falhou',
    skipped: 'Ignorado',
};

const RECIPIENT_STATUS_TABS = [
    { value: '', label: 'Todos' },
    { value: 'sent', label: 'Enviados' },
    { value: 'failed', label: 'Falharam' },
    { value: 'pending', label: 'Pendentes' },
    { value: 'skipped', label: 'Ignorados' },
];

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
}

/* Mesma distinção usada no resto da tela de clientes — sem `response`, o
   problema é a conexão do produtor, não algo que o backend explique. */
function getErrorMessage(err, fallback) {
    if (!err?.response) {
        return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
    return err?.response?.data?.message || fallback;
}

function audienceName(campaign) {
    if (campaign.audience_type === 'event_participants') {
        return `Participantes de ${campaign.event?.name || 'evento removido'}`;
    }
    if (campaign.audience_type === 'event_combination') {
        const names = (campaign.events || []).map(event => event.name);
        return `Combinação de ${names.length} eventos${names.length ? `: ${names.join(', ')}` : ''}`;
    }
    return 'Todos os clientes';
}

function audienceLabel(campaign) {
    if (!campaign) return '';
    return campaign.paid_only ? `${audienceName(campaign)} · só ingressos pagos` : audienceName(campaign);
}

/* Montado só enquanto o DashCollapse está aberto (ver EmailCampaignsPanel),
   então cada abertura começa com estado limpo e busca a campanha de novo. */
export default function EmailCampaignDetailPanel({ campaignId }) {
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [recipients, setRecipients] = useState([]);
    const [recipientsLoading, setRecipientsLoading] = useState(true);
    const [recipientsError, setRecipientsError] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        setStatusFilter('');
        setPage(1);

        emailCampaignsApi.get(campaignId).then(({ data }) => {
            if (!active) return;
            setCampaign(data?.data || null);
        }).catch(err => {
            if (!active) return;
            setError(getErrorMessage(err, 'Não foi possível carregar a campanha.'));
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => { active = false; };
    }, [campaignId]);

    useEffect(() => {
        let active = true;
        setRecipientsLoading(true);
        setRecipientsError(null);

        emailCampaignsApi.getRecipients(campaignId, { status: statusFilter || undefined, page }).then(({ data }) => {
            if (!active) return;
            setRecipients(data?.data || []);
            setLastPage(data?.meta?.last_page || 1);
        }).catch(err => {
            if (!active) return;
            setRecipientsError(getErrorMessage(err, 'Não foi possível carregar os destinatários.'));
        }).finally(() => {
            if (active) setRecipientsLoading(false);
        });

        return () => { active = false; };
    }, [campaignId, statusFilter, page]);

    function changeStatusFilter(value) {
        setStatusFilter(value);
        setPage(1);
    }

    const counts = campaign?.recipient_counts || { pending: 0, sent: 0, failed: 0, skipped: 0 };
    const total = campaign?.total_recipients || 0;
    const sentPct = total > 0 ? Math.round((campaign.sent_count / total) * 100) : 0;
    const failedPct = total > 0 ? Math.round((campaign.failed_count / total) * 100) : 0;

    return (
        <Wrapper>
            {loading && (
                <FullyCentered style={{ padding: '60px 0' }}>
                    <Loader size='small' />
                </FullyCentered>
            )}

            {!loading && error && (
                <EmptyState>
                    <Text type='bold' color={colors.danger}>Não foi possível carregar</Text>
                    <Text name='small' color={colors.nightLight} marginTop='6px'>{error}</Text>
                </EmptyState>
            )}

            {!loading && !error && campaign && (
                <>
                    <Header>
                        <AudienceIconBadge>
                            {(() => {
                                const AudienceIcon = AUDIENCE_ICONS[campaign.audience_type] || Users;
                                return <AudienceIcon size={17} strokeWidth={2.1} />;
                            })()}
                        </AudienceIconBadge>
                        <HeaderMain>
                            <Subject>{campaign.subject}</Subject>
                            <Meta>{audienceLabel(campaign)}</Meta>
                        </HeaderMain>
                        <StatusBadge $status={campaign.status}>
                            {STATUS_LABELS[campaign.status] || campaign.status}
                        </StatusBadge>
                    </Header>

                    <DatesRow>
                        {campaign.status === 'scheduled' && campaign.scheduled_at && (
                            <DateChip><CalendarClock size={13} />Agendada para {formatDateTime(campaign.scheduled_at)}</DateChip>
                        )}
                        <DateChip><Clock size={13} />Criada em {formatDateTime(campaign.created_at)}</DateChip>
                        {campaign.sent_at && (
                            <DateChip><Send size={13} />Concluída em {formatDateTime(campaign.sent_at)}</DateChip>
                        )}
                    </DatesRow>

                    {total > 0 && (
                        <ProgressWrapper>
                            <ProgressBar>
                                <ProgressFill $pct={sentPct} />
                                <ProgressFillFailed $pct={failedPct} $offset={sentPct} />
                            </ProgressBar>
                            <ProgressLabel>
                                {campaign.sent_count} de {total} enviados
                                {campaign.failed_count > 0 ? ` · ${campaign.failed_count} falharam` : ''}
                            </ProgressLabel>
                        </ProgressWrapper>
                    )}

                    <StatsGrid>
                        <StatBox>
                            <StatValue>{total}</StatValue>
                            <StatLabel>Total</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue $tone='success'>{counts.sent}</StatValue>
                            <StatLabel>Enviados</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue $tone='danger'>{counts.failed}</StatValue>
                            <StatLabel>Falharam</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue>{counts.pending}</StatValue>
                            <StatLabel>Pendentes</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue>{counts.skipped}</StatValue>
                            <StatLabel>Ignorados</StatLabel>
                        </StatBox>
                    </StatsGrid>

                    <SectionLabel>Conteúdo</SectionLabel>
                    <EmailPreviewFrame subject={campaign.subject} bodyHtml={campaign.body} />

                    <SectionLabel>Destinatários</SectionLabel>
                    <TabsRow>
                        {RECIPIENT_STATUS_TABS.map(tab => (
                            <Tab
                                key={tab.value}
                                type='button'
                                $active={statusFilter === tab.value}
                                onClick={() => changeStatusFilter(tab.value)}
                            >
                                {tab.label}
                            </Tab>
                        ))}
                    </TabsRow>

                    {recipientsLoading && <StateText>Carregando...</StateText>}

                    {!recipientsLoading && recipientsError && (
                        <StateText $error>{recipientsError}</StateText>
                    )}

                    {!recipientsLoading && !recipientsError && recipients.length === 0 && (
                        <StateText>Nenhum destinatário{statusFilter ? ' com esse status' : ''}.</StateText>
                    )}

                    {!recipientsLoading && !recipientsError && recipients.length > 0 && (
                        <>
                            <TableWrapper>
                                <RecipientsTable>
                                    <thead>
                                        <tr>
                                            <Th>E-mail</Th>
                                            <Th>Status</Th>
                                            <Th $hideOnMobile>Detalhe</Th>
                                            <Th $hideOnMobile>Enviado em</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recipients.map(recipient => (
                                            <tr key={recipient.id}>
                                                <Td title={recipient.email}>{recipient.email}</Td>
                                                <Td>
                                                    <RecipientStatus $status={recipient.status}>
                                                        {RECIPIENT_STATUS_LABELS[recipient.status] || recipient.status}
                                                    </RecipientStatus>
                                                </Td>
                                                <Td $hideOnMobile title={recipient.error_message || ''}>
                                                    {recipient.error_message || '—'}
                                                </Td>
                                                <Td $hideOnMobile>{formatDateTime(recipient.sent_at)}</Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </RecipientsTable>
                            </TableWrapper>

                            {lastPage > 1 && (
                                <PaginationBar>
                                    <PageInfo>Página {page} de {lastPage}</PageInfo>
                                    <PaginationButtons>
                                        <PageBtn type='button' onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                                            <ChevronLeft size={14} />
                                        </PageBtn>
                                        <PageBtn type='button' onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>
                                            <ChevronRight size={14} />
                                        </PageBtn>
                                    </PaginationButtons>
                                </PaginationBar>
                            )}
                        </>
                    )}
                </>
            )}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 200px;
    padding: 12px 22px 22px;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 12px 6px 22px;
    }
`;

const EmptyState = styled.div`
    padding: 60px 24px;
    text-align: center;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 12px;
`;

const AudienceIconBadge = styled.span`
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${colors.neutralBackground};
    color: ${colors.night};
`;

const HeaderMain = styled.div`
    min-width: 0;
    flex: 1;
`;

const Subject = styled.h3`
    margin: 0 0 4px;
    font-size: 17px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const Meta = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const StatusBadge = styled.span`
    flex-shrink: 0;
    padding: 5px 12px;
    border-radius: 50px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    background: ${({ $status }) => $status === 'failed'
        ? 'rgba(231, 80, 90, 0.12)'
        : $status === 'canceled'
            ? colors.neutralBackground
            : panelAccentAlpha(colors.secondaryPurple, '12')};
    color: ${({ $status }) => $status === 'failed'
        ? colors.dangerDark
        : $status === 'canceled'
            ? colors.nightLight
            : panelAccent(colors.secondaryPurple)};
`;

const DatesRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
`;

const DateChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.nightLight};
    background: ${colors.neutralBackground};
    padding: 5px 10px;
    border-radius: 50px;
`;

const ProgressWrapper = styled.div`
    margin-bottom: 18px;
`;

const ProgressBar = styled.div`
    position: relative;
    height: 8px;
    border-radius: 50px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const ProgressFill = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ $pct }) => $pct}%;
    background: ${panelAccent(colors.secondaryPurple)};
    transition: width 0.3s ease;
`;

const ProgressFillFailed = styled.div`
    position: absolute;
    left: ${({ $offset }) => $offset}%;
    top: 0;
    bottom: 0;
    width: ${({ $pct }) => $pct}%;
    background: ${colors.danger};
`;

const ProgressLabel = styled.span`
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: ${colors.nightLight};
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-bottom: 22px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const StatBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 12px 8px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
`;

const StatValue = styled.span`
    font-size: 18px;
    font-weight: 700;
    color: ${({ $tone }) => $tone === 'success' ? colors.successDark : $tone === 'danger' ? colors.dangerDark : colors.nightDark};
`;

const StatLabel = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SectionLabel = styled.h4`
    margin: 20px 0 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.night};
`;

const TabsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
`;

const Tab = styled.button`
    padding: 6px 12px;
    border-radius: 50px;
    border: 1.5px solid ${({ $active }) => $active ? panelAccent(colors.secondaryPurple) : colors.neutralDark};
    background: ${({ $active }) => $active ? panelAccentAlpha(colors.secondaryPurple, '12') : '#fff'};
    color: ${({ $active }) => $active ? panelAccent(colors.secondaryPurple) : colors.nightLight};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const StateText = styled.p`
    text-align: center;
    color: ${({ $error }) => $error ? colors.danger : colors.nightLight};
    font-size: 13px;
    padding: 24px;
    margin: 0;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const RecipientsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 8px 10px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Td = styled.td`
    padding: 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    border-bottom: 1px solid ${colors.neutralDark};
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const RecipientStatus = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: ${({ $status }) => $status === 'failed'
        ? colors.dangerDark
        : $status === 'sent'
            ? colors.successDark
            : colors.nightLight};
`;

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 12px;
`;

const PageInfo = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const PaginationButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PageBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${colors.neutralBackground};
    }
`;
